import searchFill from "@iconify/icons-eva/search-fill";
import { Icon } from "@iconify/react";
import {
  Box,
  Card,
  CardHeader,
  Chip,
  CircularProgress,
  IconButton,
  InputAdornment,
  Pagination,
  Stack,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { GridFlexEnd, RowCell, SearchStyle, TabCell } from "../../components/styled";
import Page from "../../core/components/Page";
import HeaderBreadcrumbs from "../../core/components/HeaderBreadcrumbs";
import {
  getAccessAuditSummary,
  type RecordAccessAuditSummary,
} from "../../services/record.service";
import { PATH_DASHBOARD } from "../../routes/paths";
import { useSnackbar } from "notistack";

function formatActivityDate(iso: string): string {
  const d = new Date(iso);
  const now = new Date();
  const diffMs = now.getTime() - d.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  if (diffDays === 0) {
    return d.toLocaleTimeString("tr-TR", { hour: "2-digit", minute: "2-digit" });
  }
  if (diffDays < 7) {
    return d.toLocaleDateString("tr-TR", { weekday: "short", hour: "2-digit", minute: "2-digit" });
  }
  return d.toLocaleDateString("tr-TR", {
    day: "numeric",
    month: "short",
    year: d.getFullYear() !== now.getFullYear() ? "numeric" : undefined,
  });
}

export default function AccessAuditList() {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const [entries, setEntries] = useState<RecordAccessAuditSummary[]>([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [pendingOnly, setPendingOnly] = useState(false);
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [loading, setLoading] = useState(false);
  const pageSize = 20;

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setSearch(searchInput);
      setPage(1);
    }, 400);
    return () => window.clearTimeout(timer);
  }, [searchInput]);

  const loadSummary = useCallback(async () => {
    setLoading(true);
    try {
      const response = await getAccessAuditSummary({
        pageNumber: page,
        pageSize,
        pendingOnly,
        search: search || undefined,
      });
      const data = response?.data?.data;
      setEntries(data?.result ?? []);
      setTotalPages(data?.pager?.totalPages ?? 1);
    } catch {
      enqueueSnackbar("Erişim denetimi yüklenemedi.", { variant: "error" });
      setEntries([]);
    } finally {
      setLoading(false);
    }
  }, [page, pendingOnly, search, enqueueSnackbar]);

  useEffect(() => {
    void loadSummary();
  }, [loadSummary]);

  const pendingOnPage = useMemo(
    () => entries.reduce((sum, e) => sum + e.pending_count, 0),
    [entries],
  );

  const hasActiveFilters = pendingOnly || searchInput.length > 0;

  const goToDetail = (recordId: number) => {
    navigate(PATH_DASHBOARD.reports.auditDetail(recordId));
  };

  const clearFilters = () => {
    setPage(1);
    setPendingOnly(false);
    setSearchInput("");
    setSearch("");
  };

  return (
    <Page title="Erişim denetimi">
      <HeaderBreadcrumbs
        heading="Erişim denetimi"
        links={[
          { name: "Dashboard", href: PATH_DASHBOARD.general.app },
          { name: "Raporlar", href: PATH_DASHBOARD.reports.root },
          { name: "Erişim denetimi" },
        ]}
      />

      <Card>
        <Stack
          direction={{ xs: "column", md: "row" }}
          alignItems={{ xs: "stretch", md: "center" }}
          justifyContent="space-between"
          spacing={2}
          sx={{ px: 2, pt: 2, pb: 1 }}
        >
          <CardHeader
            title="Kayıtlar"
            subheader="Paylaşım ve erişim isteklerini kayıt bazında yönetin"
            sx={{ p: 0, flex: 1 }}
          />
          <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap>
            <SearchStyle
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="Kayıt ara..."
              size="small"
              startAdornment={
                <InputAdornment position="start">
                  <Box component={Icon} icon={searchFill} sx={{ color: "text.disabled", width: 20 }} />
                </InputAdornment>
              }
            />
            <Chip
              icon={<Icon icon="mdi:clock-outline" width={18} />}
              label="Bekleyenler"
              clickable
              variant={pendingOnly ? "filled" : "outlined"}
              color={pendingOnly ? "warning" : "default"}
              onClick={() => {
                setPage(1);
                setPendingOnly((v) => !v);
              }}
            />
            {hasActiveFilters ? (
              <Tooltip title="Filtreleri temizle">
                <IconButton size="small" onClick={clearFilters} aria-label="Filtreleri temizle">
                  <Icon icon="mdi:filter-off-outline" width={22} />
                </IconButton>
              </Tooltip>
            ) : null}
          </Stack>
        </Stack>

        {!loading && entries.length > 0 ? (
          <Stack direction="row" spacing={1} sx={{ px: 3, pb: 1 }} flexWrap="wrap" useFlexGap>
            <Chip
              size="small"
              variant="outlined"
              label={`${entries.length} kayıt`}
              icon={<Icon icon="mdi:file-document-multiple-outline" width={16} />}
            />
            {pendingOnPage > 0 ? (
              <Chip
                size="small"
                color="warning"
                variant="outlined"
                label={`${pendingOnPage} bekleyen istek`}
                icon={<Icon icon="mdi:account-clock-outline" width={16} />}
              />
            ) : null}
          </Stack>
        ) : null}

        <TableContainer sx={{ minWidth: 720 }}>
          <Table>
            <TableHead>
              <TableRow>
                <TabCell>Kayıt</TabCell>
                <TabCell align="center" sx={{ minWidth: 100, maxWidth: 120 }}>
                  Paylaşılan
                </TabCell>
                <TabCell align="center" sx={{ minWidth: 100, maxWidth: 120 }}>
                  Bekleyen
                </TabCell>
                <TabCell sx={{ minWidth: 140 }}>Son aktivite</TabCell>
                <TabCell align="right" sx={{ minWidth: 56, maxWidth: 72 }} />
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <RowCell colSpan={5}>
                    <Stack alignItems="center" py={6} spacing={2}>
                      <CircularProgress size={32} />
                      <Typography variant="body2" color="text.secondary">
                        Yükleniyor...
                      </Typography>
                    </Stack>
                  </RowCell>
                </TableRow>
              ) : entries.length === 0 ? (
                <TableRow>
                  <RowCell colSpan={5}>
                    <Stack alignItems="center" py={6} spacing={1.5}>
                      <Icon
                        icon="mdi:shield-account-outline"
                        width={48}
                        style={{ opacity: 0.35 }}
                      />
                      <Typography variant="subtitle1" color="text.secondary">
                        Kayıt bulunamadı
                      </Typography>
                      <Typography variant="body2" color="text.disabled" textAlign="center">
                        {hasActiveFilters
                          ? "Filtreleri değiştirin veya temizleyin."
                          : "Erişim paylaşımı veya isteği olan kayıtlar burada listelenir."}
                      </Typography>
                      {hasActiveFilters ? (
                        <Chip label="Filtreleri temizle" clickable onClick={clearFilters} size="small" />
                      ) : null}
                    </Stack>
                  </RowCell>
                </TableRow>
              ) : (
                entries.map((row) => (
                  <TableRow
                    key={row.record_id}
                    hover
                    onClick={() => goToDetail(row.record_id)}
                    sx={{ cursor: "pointer" }}
                  >
                    <RowCell>
                      <Stack direction="row" spacing={1.5} alignItems="center">
                        <Box
                          sx={{
                            width: 36,
                            height: 36,
                            borderRadius: 1,
                            bgcolor: "action.hover",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            flexShrink: 0,
                          }}
                        >
                          <Icon icon="mdi:file-document-outline" width={20} />
                        </Box>
                        <Typography variant="subtitle2" noWrap title={row.record_name}>
                          {row.record_name}
                        </Typography>
                      </Stack>
                    </RowCell>
                    <RowCell align="center">
                      <Chip
                        size="small"
                        variant="outlined"
                        label={row.shared_count}
                        icon={<Icon icon="mdi:account-multiple-outline" width={14} />}
                        sx={{ minWidth: 56 }}
                      />
                    </RowCell>
                    <RowCell align="center">
                      {row.pending_count > 0 ? (
                        <Chip
                          label={row.pending_count}
                          size="small"
                          color="warning"
                          icon={<Icon icon="mdi:clock-outline" width={14} />}
                        />
                      ) : (
                        <Typography variant="caption" color="text.disabled">
                          —
                        </Typography>
                      )}
                    </RowCell>
                    <RowCell>
                      <Tooltip title={new Date(row.last_activity_at).toLocaleString("tr-TR")}>
                        <Typography variant="body2" color="text.secondary">
                          {formatActivityDate(row.last_activity_at)}
                        </Typography>
                      </Tooltip>
                    </RowCell>
                    <RowCell align="right" onClick={(e) => e.stopPropagation()}>
                      <Tooltip title="Erişimi yönet">
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => goToDetail(row.record_id)}
                          aria-label="Erişimi yönet"
                        >
                          <Icon icon="mdi:chevron-right" width={24} />
                        </IconButton>
                      </Tooltip>
                    </RowCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {totalPages > 1 ? (
          <GridFlexEnd sx={{ m: 3 }}>
            <Pagination
              count={totalPages}
              page={page}
              onChange={(_, value) => setPage(value)}
              color="primary"
              shape="rounded"
            />
          </GridFlexEnd>
        ) : (
          <Box sx={{ pb: 2 }} />
        )}
      </Card>
    </Page>
  );
}
